import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  Users as UsersIcon,
  Plus,
  Trash2,
  KeyRound,
  Loader2,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import type { AppUser } from "@/types/content";
import {
  adminCreateUser,
  adminDeleteUser,
  adminResetPassword,
  adminUpdateUserRole,
  type AdminUserRole,
} from "@/services/adminUsersService";

const ROLE_OPTIONS: AdminUserRole[] = ["admin", "editor", "reader"];

export default function UsersPage() {
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AppUser | null>(null);
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      if (!supabase) return [] as AppUser[];
      const { data, error } = await supabase
        .from("app_users")
        .select("*")
        .order("joined_at", { ascending: false });
      if (error) throw error;
      return (data || []) as AppUser[];
    },
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin-users"] });

  const updateRoleMut = useMutation({
    mutationFn: ({ user_id, role }: { user_id: string; role: AdminUserRole }) =>
      adminUpdateUserRole(user_id, role),
    onSuccess: () => {
      toast({ title: "Rol actualizado" });
      invalidate();
    },
    onError: (e: Error) =>
      toast({ title: "Error al actualizar rol", description: e.message, variant: "destructive" }),
  });

  const deleteMut = useMutation({
    mutationFn: (user_id: string) => adminDeleteUser(user_id),
    onSuccess: () => {
      toast({ title: "Usuario eliminado" });
      setDeleteTarget(null);
      invalidate();
    },
    onError: (e: Error) =>
      toast({ title: "Error al eliminar", description: e.message, variant: "destructive" }),
  });

  const resetMut = useMutation({
    mutationFn: (email: string) => adminResetPassword(email),
    onSuccess: () =>
      toast({
        title: "Email de recuperación enviado",
        description: "El usuario recibirá un correo con instrucciones.",
      }),
    onError: (e: Error) =>
      toast({ title: "Error al enviar reset", description: e.message, variant: "destructive" }),
  });

  const filtered = users.filter(
    (u) =>
      !search ||
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-heading font-bold text-foreground">Usuarios</h1>
        <Button onClick={() => setCreateOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" /> Nuevo usuario
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nombre o email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-16 text-muted-foreground">
          <UsersIcon className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p>Cargando usuarios...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <UsersIcon className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p>No hay usuarios.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Usuario</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="w-40">Rol</TableHead>
                <TableHead>Registro</TableHead>
                <TableHead>Último activo</TableHead>
                <TableHead className="w-28 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((u) => {
                const isSelf = u.id === currentUser?.id;
                return (
                  <TableRow key={u.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={u.avatar_url} />
                          <AvatarFallback className="text-xs">
                            {(u.name || u.email || "U")
                              .split(/[\s@]+/)
                              .map((n) => n[0])
                              .join("")
                              .slice(0, 2)
                              .toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <span className="font-medium text-foreground">{u.name || "—"}</span>
                          {isSelf && (
                            <Badge variant="secondary" className="ml-2 text-[10px]">tú</Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      <Select
                        value={u.role || "reader"}
                        disabled={updateRoleMut.isPending}
                        onValueChange={(role) =>
                          updateRoleMut.mutate({ user_id: u.id, role: role as AdminUserRole })
                        }
                      >
                        <SelectTrigger className="h-8 w-32 capitalize">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLE_OPTIONS.map((r) => (
                            <SelectItem key={r} value={r} className="capitalize">
                              {r}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {u.joined_at ? new Date(u.joined_at).toLocaleDateString("es-ES") : "-"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {u.last_active_at
                        ? new Date(u.last_active_at).toLocaleDateString("es-ES")
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Enviar email de recuperación"
                          disabled={!u.email || resetMut.isPending}
                          onClick={() => resetMut.mutate(u.email)}
                        >
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          title={isSelf ? "No puedes eliminarte" : "Eliminar usuario"}
                          disabled={isSelf}
                          onClick={() => setDeleteTarget(u)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <p className="text-xs text-muted-foreground">{filtered.length} usuarios</p>

      <CreateUserDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={invalidate}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar a {deleteTarget?.name || deleteTarget?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción borra el usuario de auth.users y elimina en cascada su perfil,
              biblioteca, progreso y bookmarks. No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMut.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMut.isPending}
              onClick={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMut.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Eliminando...
                </>
              ) : (
                "Eliminar"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateUserDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<AdminUserRole>("reader");

  const reset = () => {
    setEmail("");
    setName("");
    setPassword("");
    setRole("reader");
  };

  const createMut = useMutation({
    mutationFn: () => adminCreateUser({ email, password, name, role }),
    onSuccess: () => {
      toast({ title: "Usuario creado", description: `${email} con rol ${role}` });
      reset();
      onOpenChange(false);
      onCreated();
    },
    onError: (e: Error) =>
      toast({ title: "Error al crear", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nuevo usuario</DialogTitle>
          <DialogDescription>
            Se creará la cuenta con email confirmado. El usuario podrá iniciar sesión inmediatamente.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            createMut.mutate();
          }}
        >
          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@dominio.com"
            />
          </div>
          <div>
            <Label htmlFor="name">Nombre</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre completo"
            />
          </div>
          <div>
            <Label htmlFor="password">Contraseña * (mín. 8)</Label>
            <Input
              id="password"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div>
            <Label>Rol</Label>
            <Select value={role} onValueChange={(v) => setRole(v as AdminUserRole)}>
              <SelectTrigger className="capitalize">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((r) => (
                  <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={createMut.isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createMut.isPending}>
              {createMut.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creando...
                </>
              ) : (
                "Crear usuario"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
